import { ExpandMoreOutlined, FilterListOutlined, SearchOutlined } from "@mui/icons-material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import {
  Box,
  Button,
  Checkbox,
  Fade,
  FormControlLabel,
  Menu,
  MenuItem,
  OutlinedInput,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { accessService } from "../../../Core/Api/services";
import { AccessUserInfos, ExternalAccessDto } from "../../../Core/Api/Types/access-models";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { getPermissionReadableName, PermissionType } from "../../../Core/Api/Types/permission-type";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { UserHasAcl } from "../../../Utils/helpers";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { useToggle } from "../../../Utils/Hooks/useToggle";
import { ModalContext } from "../../Modals";
import ModalRevokeExternalAccess, {
  ModalRevokeExternalAccessElementProps,
} from "../../Modals/ModalRevokeExternalAccess/modal-revoke-external-access";
import AccessAccordion from "../Shared/access-accordion";
import AccessAccordionDetails from "../Shared/access-accordion-details";
import AccessAccordionSummary from "../Shared/access-accordion-summary";
import ExternalAccessCard from "./external-access-card";
import ModalCreateOrUpdateExternalAccess, {
  ModalCreateOrUpdateExternalAccessElementProps,
} from "../../Modals/ModalCreateOrUpdateExternalAccess/modal-create-or-update-external-access";

interface ExternalAccessAccordionProps {
  asset: DbAssetObjectInfos;
  allUserInfos: AccessUserInfos[];
  isParentLoading: boolean;
}

const ExternalAccessAccordion = (props: ExternalAccessAccordionProps) => {
  const { t } = useTranslation();
  const { asset, allUserInfos, isParentLoading } = props;

  const userCanCreateOrUpdateExternalAccess = UserHasAcl(AclTypes.aclExternalAccessCreateOrUpdate);
  const userCanRevokeAccess = UserHasAcl(AclTypes.aclExternalAccessRevoke);

  const enqueueSnackbar = useCustomSnackbar();
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const [anchorFilterEl, setAnchorFilterEl] = React.useState<null | HTMLElement>(null);
  const [permissionsFilter, setPermissionsFilter] = React.useState<PermissionType[]>([]);
  const [textFilter, setTextFilter] = React.useState<string>("");
  const [isChildrenHovered, setIsChildrenHovered] = React.useState(false);
  const { active: externalAccessesExpanded, toggle: toggleExternalAccessExpanded } = useToggle(false);
  const [_isLoading, set_IsLoading] = React.useState(true);
  const [externalAccesses, setExternalAccesses] = React.useState<ExternalAccessDto[]>([]);

  const isLoading = React.useMemo(() => {
    return isParentLoading || _isLoading;
  }, [isParentLoading, _isLoading]);

  const getUserName = (userId: number) => {
    const user = allUserInfos.find((user) => user.id === userId);
    if (!user) return "Unknown";
    return `${user.firstName} ${user.lastName}`.trim();
  };

  const filteredExternalAccesses = React.useMemo(() => {
    return externalAccesses.filter((access) => {
      const permissionFilterMatch =
        permissionsFilter.length === 0 || permissionsFilter.every((perm) => access.permissions.includes(perm));
      const textFilterMatch =
        textFilter === "" ||
        access.email.includesAllWords(textFilter) ||
        getUserName(access.ownerId).includesAllWords(textFilter);
      return permissionFilterMatch && textFilterMatch;
    });
  }, [externalAccesses, permissionsFilter, textFilter]);

  const refreshExternalAccesses = () => {
    accessService.ListExternalAccessesByObjectId(asset.id).then((response) => {
      if (response.success) {
        setExternalAccesses(response.accesses);
        set_IsLoading(false);
      } else {
        enqueueSnackbar(response.message || t("alert:error.oopsSomethingWentWrongWithDot"), "error");
      }
    });
  };

  const handleFilterClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setAnchorFilterEl(e.currentTarget);
  };
  const handleFilterClose = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setAnchorFilterEl(null);
  };

  const isFilterPermissionChecked = (permission: PermissionType) => {
    return permissionsFilter.includes(permission);
  };

  const handlePermissionFilterChange = (permission: PermissionType) => {
    const newPermissionsFilter = permissionsFilter.includes(permission)
      ? permissionsFilter.filter((p) => p !== permission)
      : [...permissionsFilter, permission];

    setPermissionsFilter(newPermissionsFilter);
  };

  const handleCreateClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();

    openModal &&
      openModal<ModalCreateOrUpdateExternalAccessElementProps>(ModalCreateOrUpdateExternalAccess, {
        assets: [asset],
        onSuccess: refreshExternalAccesses,
      });
  };

  const handleEditClick = (externalAccess: ExternalAccessDto) => {
    openModal &&
      openModal<ModalCreateOrUpdateExternalAccessElementProps>(ModalCreateOrUpdateExternalAccess, {
        assets: [
          new DbAssetObjectInfos({
            id: externalAccess.objectId,
            name: externalAccess.objectName || "",
            type: externalAccess.objectType || ObjectType.VirtualHead,
          }),
        ],
        accessToUpdate: externalAccess,
        onSuccess: refreshExternalAccesses,
      });
  };

  const handleDeleteClick = (externalAccess: ExternalAccessDto) => {
    openModal &&
      openModal<ModalRevokeExternalAccessElementProps>(ModalRevokeExternalAccess, {
        accessId: externalAccess.id,
        asset: new DbAssetObjectInfos({
          id: externalAccess.objectId,
          name: externalAccess.objectName || "",
          type: externalAccess.objectType || ObjectType.VirtualHead,
        }),
        onSuccess: refreshExternalAccesses,
      });
  };

  React.useEffect(() => {
    refreshExternalAccesses();
  }, [asset]);

  return (
    <>
      {!isLoading ? (
        <AccessAccordion expanded={externalAccessesExpanded} isChildrenHovered={isChildrenHovered}>
          <AccessAccordionSummary
            expandIcon={<ExpandMoreOutlined color="inherit" />}
            onClick={toggleExternalAccessExpanded}
          >
            <Stack direction="row" justifyContent="space-between" width="100%">
              <Stack direction="row" pl={0.5} spacing={1} alignItems="center">
                <LoginOutlinedIcon color="info" />
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2">{t("page:body.externalAccess")}</Typography>
                  <Typography variant="body2" color="text.tier">
                    {externalAccesses.length} user{externalAccesses.length === 1 ? "" : "s"}
                  </Typography>
                </Stack>
              </Stack>
              <Box>
                {userCanCreateOrUpdateExternalAccess && (
                  <Fade in={externalAccessesExpanded}>
                    <Button variant="contained" startIcon={<AddOutlinedIcon />} onClick={handleCreateClick}>
                      {t("common:create")}
                    </Button>
                  </Fade>
                )}
              </Box>
            </Stack>
          </AccessAccordionSummary>
          <AccessAccordionDetails>
            {externalAccesses.length === 0 && (
              <Stack spacing={1} justifyContent="center" alignItems="center">
                <Typography variant="body1" color="text.tier">
                  {t("page:body.noExternalAccesses")}
                </Typography>
              </Stack>
            )}
            {externalAccesses.length > 0 && (
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1}>
                  <OutlinedInput
                    placeholder={t("field:placeholder.textFilterForExternalAccess")}
                    fullWidth
                    sx={{ borderRadius: 1 }}
                    endAdornment={<SearchOutlined />}
                    value={textFilter}
                    onChange={(e) => setTextFilter(e.target.value)}
                  />
                  <Button variant="icon" onClick={handleFilterClick}>
                    <FilterListOutlined />
                  </Button>
                  <Menu
                    anchorEl={anchorFilterEl}
                    open={Boolean(anchorFilterEl)}
                    onClose={handleFilterClose}
                    anchorOrigin={{
                      vertical: "center",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    {GlobalConst.EXTERNAL_ACCESS_ALLOWED_PERMISSIONS.map((permission) => (
                      <MenuItem key={permission} dense sx={{ padding: 0, paddingLeft: 1.5 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={isFilterPermissionChecked(permission)}
                              onChange={() => handlePermissionFilterChange(permission)}
                            />
                          }
                          label={getPermissionReadableName(permission)}
                        />
                      </MenuItem>
                    ))}
                  </Menu>
                </Stack>
                {filteredExternalAccesses.map((externalAccess) => (
                  <ExternalAccessCard
                    key={externalAccess.id}
                    access={externalAccess}
                    creatorUserName={getUserName(externalAccess.ownerId)}
                    userCanEditAccess={userCanCreateOrUpdateExternalAccess}
                    userCanRevokeAccess={userCanRevokeAccess}
                    onEdit={() => handleEditClick(externalAccess)}
                    onDelete={() => handleDeleteClick(externalAccess)}
                    onMouseEnterCard={() => setIsChildrenHovered(true)}
                    onMouseLeaveCard={() => setIsChildrenHovered(false)}
                  />
                ))}
              </Stack>
            )}
          </AccessAccordionDetails>
        </AccessAccordion>
      ) : (
        <Skeleton variant="rounded" height={64} />
      )}
    </>
  );
};

export default ExternalAccessAccordion;
